import type { CampaignClaimPolicy, ClaimEligibilityInput, ClaimEligibilityResult } from './model';

type PlainRecord = Record<string, unknown>;

function record(value: unknown): PlainRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as PlainRecord
    : null;
}

function positiveInteger(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const normalized = Math.trunc(value);
  return normalized >= 1 ? normalized : null;
}

function taskTypeEntry(campaignData: PlainRecord | undefined, taskTypeId: string): PlainRecord | null {
  if (!campaignData) return null;
  if (Array.isArray(campaignData.taskTypes)) {
    const entry = campaignData.taskTypes.find((item) => {
      const data = record(item);
      return data?.taskTypeId === taskTypeId;
    });
    return record(entry);
  }
  if (campaignData.taskTypeId === taskTypeId) return campaignData;
  return null;
}

export interface TaskTypeClaimSettings {
  claimingPaused?: boolean;
  domains?: Array<{ domainId: string; claimingPaused?: boolean }>;
}

export function isCampaignClaimEligible(
  campaignData: { archivedAt?: unknown; activeExpertIds?: unknown } | null | undefined,
  actor: { isAdmin?: boolean; expertId?: string | null },
): boolean {
  if (!campaignData || campaignData.archivedAt != null) return false;
  if (actor.isAdmin === true) return true;
  return typeof actor.expertId === 'string'
    && Array.isArray(campaignData.activeExpertIds)
    && campaignData.activeExpertIds.includes(actor.expertId);
}

/** Read pause state from an already-normalized task-type entry. */
export function isTaskTypeClaimingPaused(
  entry: TaskTypeClaimSettings,
  domainId: string | null = null,
): boolean {
  if (domainId) {
    return entry.domains?.find((domain) => domain.domainId === domainId)?.claimingPaused === true;
  }
  return entry.claimingPaused === true;
}

/** Read all campaign claim gates with the legacy single-type fallback. */
export function readCampaignClaimPolicy(
  campaignData: PlainRecord | undefined,
  taskTypeId: string,
  domainId: string | null = null,
  isTrusted = false,
): CampaignClaimPolicy {
  const entry = taskTypeEntry(campaignData, taskTypeId);

  let claimingPaused = false;
  if (entry) {
    if (domainId) {
      const domains = Array.isArray(entry.domains) ? entry.domains : [];
      const domain = domains.find((item) => record(item)?.domainId === domainId);
      claimingPaused = record(domain)?.claimingPaused === true;
    } else {
      claimingPaused = entry.claimingPaused === true;
    }
  }

  return {
    claimingPaused,
    firstApproval: campaignData?.requireFirstApprovalBeforeMoreClaims === true,
    maxTasks: (isTrusted ? positiveInteger(campaignData?.maxTasksPerTrustedUser) : null)
      ?? positiveInteger(campaignData?.maxTasksPerUser),
    maxClaimedTasks: positiveInteger(campaignData?.maxClaimedTasksPerUser),
  };
}

/** Evaluate all claim gates in the same order as the current transaction facade. */
export function evaluateClaimEligibility(input: ClaimEligibilityInput): ClaimEligibilityResult {
  const { policy } = input;
  if (policy.claimingPaused && input.bypassClaimingPaused !== true) {
    return { allowed: false, reason: 'claiming-paused' };
  }
  if (
    policy.firstApproval
    && (input.approvedTaskCount ?? 0) === 0
    && (input.openTaskCount ?? 0) > 0
  ) {
    return { allowed: false, reason: 'first-approval-required-claim' };
  }
  if (policy.maxTasks != null && (input.totalTaskCount ?? 0) >= policy.maxTasks) {
    return { allowed: false, reason: 'campaign-max-tasks-per-user' };
  }
  if (policy.maxClaimedTasks != null && input.activeTaskCount >= policy.maxClaimedTasks) {
    return { allowed: false, reason: 'campaign-max-claimed-tasks-per-user' };
  }
  return { allowed: true };
}
